import { Controller } from "react-hook-form";

import {
  DatePicker,
  ELEMENT_WRAPPER_MB,
  ElementWrapper,
  ErrorMessage,
  InputText,
  Label,
  LabelCheckbox,
  Select,
  SelectCountry,
  createValidation,
  getErrorMessage,
} from "@up/form";

import type { DataTypes, TErrorMessageKey } from "@up/form";
import type { FieldErrors, UseFormRegister } from "react-hook-form";

type FormElementProps = {
  formErrors: FieldErrors;
  formValues: DataTypes.FormValues;
  register: UseFormRegister<any>;
};

export const StudentFirstName = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "firstName";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Student First Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const StudentLastName = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "lastName";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Student Last Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const StudentEmail = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "email";
  const VALIDATION = { email: true, presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required text="Student Personal Email" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

type StudentAgeProps = {
  control: any;
  formErrors: FieldErrors;
  formValues: DataTypes.FormValues;
  register: UseFormRegister<any>;
};
export const StudentAge = function ({
  control,
  formErrors,
  formValues,
  register,
}: StudentAgeProps) {
  const KEY = "age";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];
  const OPTIONS = [
    {
      value: "13",
      key: "13",
    },
    {
      value: "14",
      key: "14",
    },
    {
      value: "15",
      key: "15",
    },
    {
      value: "16",
      key: "16",
    },
    {
      value: "17",
      key: "17",
    },
    {
      value: "18",
      key: "18+",
    },
  ];

  const transform = {
    input: (value: any) =>
      isNaN(value) || value === "0" ? "" : value.toString(),
    output: (e: any) => {
      // Use this to transform to number if needed in the future
      const output = e.target.value;
      //const output = parseInt(e.target.value, 10)
      return isNaN(output) ? "0" : output;
    },
  };

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} text="Student Age" />
      <Controller
        defaultValue=""
        control={control}
        name="age"
        render={({ field }) => {
          return (
            <Select
              data={OPTIONS}
              displayKey="key"
              hasErrors={!!ERRORS}
              name="age"
              placeholderText="Please select"
              registerProps={register(
                KEY,
                createValidation(VALIDATION, formValues),
              )}
              value={transform.input(field.value)}
              valueKey="value"
              style={{ maxWidth: "200px" }}
              onChange={(e: any) => {
                return field.onChange(transform.output(e));
              }}
            />
          );
        }}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

type TOUPPAcceptProps = {
  formErrors: FieldErrors;
  formValues: DataTypes.FormValues;
  institutionName: string;
  register: UseFormRegister<any>;
};

export const TOUPPAccept = function ({
  formErrors,
  formValues,
  institutionName,
  register,
}: TOUPPAcceptProps) {
  const KEY = "touppAccept";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <LabelCheckbox
        Label={
          <>
            I agree to {institutionName}'s{" "}
            <a
              className="kup-link"
              href="/terms-of-use"
              rel="noreferrer noopener"
              target="_blank"
            >
              Terms of Use
            </a>{" "}
            and{" "}
            <a
              className="kup-link"
              href="/privacy-policy"
              rel="noreferrer noopener"
              target="_blank"
            >
              Privacy Policy
            </a>
          </>
        }
        required={true}
      >
        <input
          type="checkbox"
          value="yes"
          {...register(KEY, createValidation(VALIDATION, formValues))}
        />
      </LabelCheckbox>
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

type StudentDOBProps = {
  formErrors: FieldErrors;
  formValues: DataTypes.FormValues;
  register: UseFormRegister<any>;
};

export const StudentDOB = function ({
  formErrors,
  formValues,
  register,
}: StudentDOBProps) {
  const KEY = "dob";
  const ERRORS = formErrors[KEY];
  const VALIDATION = { presence: true };

  const now = new Date();
  const thirteenYearsAgo = new Date(
    now.getFullYear() - 13,
    now.getMonth(),
    now.getDate(),
  );

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required text="Date of Birth" />
      <DatePicker
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
        max={thirteenYearsAgo.toLocaleDateString()}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const StudentCountry = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "country";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Country" />
      <SelectCountry
        hasErrors={!!ERRORS}
        name={KEY}
        placeholderText="Please Select"
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const StudentZip = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "zipCode";
  const isUS = formValues["country"] === "United States";
  const VALIDATION = isUS ? { presence: true } : {};
  const ERRORS = formErrors[KEY];
  const zipLabel = isUS ? "Zip Code" : "Postal Code";

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={isUS} text={zipLabel} />
      <InputText
        className="kup-input-text-zipcode"
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};
